import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DatasetDataInfoResponse, PaginatedParams } from "api/model/dataset/DatasetDataInfoResponse";
import { APIResponse } from "services/HttpService";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import { DatasetDataApi } from "../DatasetDataApi";
import { DatasetsApi } from "../DatasetsApi";
import { BaseDatasetDTO } from "../model/dataset/BaseDatasetDTO";
import { DatasetDataLagLeadRequest } from "../model/requests";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

export const useGetDatasetCategories = () =>
  useQueryWrapper([QueryKey.DATASET_CATEGORIES], () => DatasetsApi.getDatasetCategories());

export const useGetDatasetInfo = (identifier: string | null, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, identifier],
    () => DatasetsApi.getDatasetInfo(identifier),
    null,
    options
  );

export const useGetDatasetsInfo = (identifiers: Array<string | null>, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, identifiers.join(",")],
    () => DatasetsApi.getDatasetsInfo(identifiers),
    null,
    options
  );

export const useSearchDatasets = (query: string, limit: number, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, query, limit.toString()],
    () => DatasetsApi.queryDatasets(query, limit),
    null,
    options
  );

export const useGetBaseDataset = (identifier: string | null, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.BASE_DATASETS, identifier],
    () => DatasetsApi.getBaseDataset(identifier),
    null,
    options
  );

export const useGetPaginatedDatasets = (
  params: PaginatedParams,
  options?: IQueryOptions
) =>{
  const {query, page, size, identifiers} = params;
  return useQueryWrapper(
    [QueryKey.PAGINATED_DATASETS, query ?? identifiers, `${page}[${size}]`],
    () => DatasetsApi.getPaginatedDatasetsList(query, page, size, identifiers),
    null,
    options
  )};

export const useGetDatasetsCount = (options?: IQueryOptions) =>
  useQueryWrapper([QueryKey.DATASETS_COUNT], () => DatasetsApi.getDatasetsCount(), null, options);

export const useGetDatasetDataInfo = (
  identifiers: Array<string>,
  config?: DatasetDataLagLeadRequest,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [QueryKey.DATASET_DATA_INFO, ...identifiers],
    () => DatasetDataApi.getDataInfo(identifiers, config),
    null,
    options
  );

export const useGetDataInfoRange = (
  identifiers: Array<string>,
  params: {
    uniqueKey?: string;
    timeStart: string;
    timeEnd?: string;
    usePromiseHandler?: ((res: APIResponse<DatasetDataInfoResponse[]>) => any) | null | undefined;
  },
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [
      QueryKey.DATASET_DATA_INFO,
      params.uniqueKey,
      ...identifiers,
      params.timeStart,
      params.timeEnd || params.timeStart,
    ],
    () => DatasetDataApi.getDataInfoRange(identifiers, params.timeStart, params.timeEnd),
    params.usePromiseHandler,
    options
  );

export const useGetRecentData = (
  identifiers: Array<string>,
  options: {
    count?: number;
    options?: IQueryOptions;
    refTime?: string;
    usePromiseHandler?:
      | ((res: APIResponse<Array<DatasetDataInfoResponse>>) => any)
      | null
      | undefined;
  }
) =>
  useQueryWrapper(
    [QueryKey.DATASET_DATA_INFO, ...identifiers, options.count?.toString(), options.refTime],
    () =>
      DatasetDataApi.getRecentData(identifiers, {
        count: options.count,
        refTime: options.refTime,
      }),
    options.usePromiseHandler,
    options.options
  );

export const useGetDefaultDatasetCategory = () =>
  useQueryWrapper([QueryKey.DEFAULT_CATEGORY], () => DatasetsApi.getDefaultDatasetCategory());

export const useClearDatasetData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (queryData: {
      identifiers: Array<String>;
      timeStart: string;
      timeEnd: string;
      clearAll: boolean;
    }) => DatasetDataApi.clearDatasetData(queryData),
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.CLEAR_DATASET], () => {
        return data;
      });
      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useCreateCategorizedDataset = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<BaseDatasetDTO>) =>
      DatasetsApi.createNewCategorizedDataset(data).then(defaultResponseHandler),
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.CATEGORIZED_DATASET], () => {
        return data;
      });
      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useUpdateDatasetInfo = (fullUpdate?: boolean) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: BaseDatasetDTO) => {
      const res = await DatasetsApi.putUpdateDatasetInfo(data, fullUpdate);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueriesData(
        { queryKey: [QueryKey.PAGINATED_DATASETS], type: "active" },
        (oldQueryData: DatasetInfo[] | undefined) => {
          if (!data) return oldQueryData;
          return oldQueryData?.map(dataset =>
            dataset.identifier === data.identifier ? data : dataset
          ) as DatasetInfo[];
        }
      );
    },
  });
};

export const useDeleteDataset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => DatasetsApi.deleteDataset(id).then(defaultResponseHandler),
    ...defaultMutationOptions,
    onSettled: () => {
      queryClient.removeQueries();
    },
  });
};


export const useGetDatasetsInfoByTags = (categoryId: number | undefined, tags: string[], options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_INFOS, categoryId, tags.join(",")],
    () => DatasetsApi.getDatasetsByTags(categoryId, tags.join(",")),
    null,
    options
  );